import { useState, useEffect } from 'react'
import AuthService from 'services/auth'
import cutileiApi from 'services/cutileiApi'
import { logApiError } from 'services/apiError'
import Dashboard from 'services/Dashboard'
import PieChart from 'components/Charts/PieChart'
import ColumnChart from 'components/Charts/ColumnChart'
import HistoryDialog from 'components/Charts/HistoryDialog'
import Select from 'components/Inputs/Select'
import { ReactComponent as Loading } from 'icons/loading2.svg'
import { DateTime } from 'luxon'

import {
  InfoContainer,
  FilterContainer,
  LoadingContainer,
  SubTitle,
  InfoText,
  LinkText
} from '../../styles'

export default function BonusesAndDiscounts ({
  bonuses,
  discounts,
  month = DateTime.now ().month,
  year = DateTime.now ().year
}) {
  const selectedDate = DateTime.local (year, month).setLocale ('pt-BR')
  const token = AuthService.getToken ()
  const businessId = AuthService.getBusinessId ()
  const periodFilterOptions = Dashboard.PERIOD_FILTERS
  const [loading, setLoading] = useState (true)
  const [isMounted, setMounted] = useState (false)
  const [periodFilter, setPeriodFilter] = useState (null)
  const [showDetails, setShowDetails] = useState (false)
  const [bonusesAndDiscounts, setBonusesAndDiscounts] = useState ([])

  const requestConfig = {
    headers: {
      'Authorization': `Bearer ${token}`
    }
  }

  useEffect (() => {
    if (isMounted) setPeriodFilter (periodFilterOptions[0])
  }, [isMounted])

  useEffect (() => {
    getData ()
  }, [periodFilter])

  const getData = async () => {
    if (showDetails) try {
      setLoading (true)

      const startDate = selectedDate.minus ({ months: periodFilter.value }).startOf ('month').toISODate ()
      const endDate = selectedDate.endOf ('month').toISODate ()

      const filters = `?business_id=${businessId}&start_date=${startDate}&end_date=${endDate}`
                    + '&professional_id=all_professionals'
        
      const { data: bonuses } = await cutileiApi.get (`/businesses/${businessId}/credits${filters}`, requestConfig)
      const { data: discounts } = await cutileiApi.get (`/businesses/${businessId}/debts${filters}`, requestConfig)

      const bonusesByMonth = Dashboard.groupEarningsByMonth (bonuses.sort (orderBonusesAndDiscounts))
      const discountsByMonth = Dashboard.groupEarningsByMonth (discounts.sort (orderBonusesAndDiscounts))
      const months = [...new Set (Object.keys ({...bonusesByMonth, ...discountsByMonth}))]
      
      setBonusesAndDiscounts ([
        ['Mês / ano', 'Bônus', 'Descontos'],
        ...months.map (month => ([
          month,
          bonusesByMonth[month]?.reduce ((total, bonus) => total + bonus.value, 0),
          discountsByMonth[month]?.reduce ((total, discount) => total + discount.value, 0)
        ]))
      ])
    } catch (error) {
      logApiError (error)
    } finally {
      setLoading (false)
    }
  }

  const orderBonusesAndDiscounts = (a, b) => (
    DateTime.fromISO (a.date).toMillis () - DateTime.fromISO (b.date).toMillis ()
  )

  const toggleDetails = () => {
    if (!isMounted) setMounted (true)
    setShowDetails (!showDetails)
  }

  if (!bonuses && !discounts || bonuses.length === 0 && discounts.length === 0) {
    return null
  }

  const monthBonusesAndDiscounts = [
    ['Origem', 'Valor (R$)'],
    ['Bônus', bonuses.reduce ((total, earning) => total + earning.value, 0)],
    ['Descontos', discounts.reduce ((total, exponse) => total + exponse.value, 0)]
  ]

  return (
    <InfoContainer>
      <SubTitle>Bônus e descontos</SubTitle>
      <PieChart
        data={monthBonusesAndDiscounts}
        options={{colors: ['#44CF6C', '#FF3939']}}
      />
      <LinkText onClick={toggleDetails}>
        Ver histórico
      </LinkText>
      <HistoryDialog
        visible={showDetails}
        title='Bônus e descontos'
        onClose={toggleDetails}
      >
        <FilterContainer>
          <Select
            name='periodFilter'
            placeholder='Período...'
            value={periodFilter}
            options={periodFilterOptions}
            onChange={(_, option) => setPeriodFilter (option)}
            containerStyles={{width: 250}}
          />
        </FilterContainer>
        {loading ? (
          <LoadingContainer>
            <Loading/>
            <InfoText>Gerando gráfico...</InfoText>
          </LoadingContainer>
        ) : (
          <ColumnChart
            data={bonusesAndDiscounts}
            options={{
              isStacked: false,
              colors: ['#44CF6C', '#FF3939'],
              chartArea: {
                top: 35, left: 110, width: '100%', height: '80%'
              },
              vAxis: {
                title: 'Valor (R$)',
                titleTextStyle: {
                  bold: true
                }
              }
            }}
          />
        )}
      </HistoryDialog>
    </InfoContainer>
  )
}
